import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Tag, Select, Space, Image, message } from "antd";
import {
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  Wallet,
  IndianRupee,
  TrendingUp,
  Briefcase,
} from "lucide-react";
import axios from "axios";
import reapi from "../apis/reapi";
import CustomPagination from "../Components/CustomPagination";

export default function History() {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setstats] = useState(null);

  const [totalRecords, setTotalRecords] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem('token')


  const fetchHistory = async (page = 1, limit = 10) => {


    try {
      setLoading(true);
      const skip = (page - 1) * limit;

      // apni API yaha lagao
      const res = await reapi.get(`/gethistorypageinate?skip=${skip}&limit=${limit}`, {
        headers: {
          "Authorization": `Beaer ${token}`
        }
      });

      setHistory(res.data?.data?.data || []);
      setstats(res.data?.data?.stats)
      setTotalRecords(res.data?.data?.total)
    } catch (error) {
      message.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage, pageSize);
  }, [currentPage, pageSize]);



  const downloadImage = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `shipless-optimized-${Date.now()}.jpg`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Image download failed");
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (_, record, index) => index + 1,
    },
    {
      title: "",
      dataIndex: "imgurl",
      width: 90,
      render: (imgurl) => (
        <Image
          src={imgurl}
          width={58}
          height={58}
          className="rounded-xl object-cover bg-slate-100"
          preview={false}
        />
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      render: (date) => {
        const d = new Date(date);

        return (
          <div>
            <div className="font-medium">
              {d.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
            <div className="text-slate-500 text-sm">
              {d.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      title: "Original Shipping",
      dataIndex: "originalshipping",
      render: (value) => <span>₹{value}</span>,
    },
    {
      title: "Optimized Shipping",
      dataIndex: "optimizedshipping",
      render: (value) => <span>₹{value}</span>,
    },
    {
      title: "You Saved",
      dataIndex: "savings",
      sorter: (a, b) => a.savings - b.savings,
      render: (value) => {
        const isSaved = Number(value) > 0;

        return (
          <div
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl font-bold ${isSaved
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-slate-50 text-slate-500 border border-slate-200"
              }`}
          >
            <span>{isSaved ? "🎉" : ""}</span>
            <span>₹{value}</span>
          </div>
        );
      },
    },
    {
      title: "Credits Used",
      dataIndex: "credits",
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Variations",
      dataIndex: "variationsChecked",
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Time",
      dataIndex: "timetake",
      render: (value) => <span>{value}s</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) =>
        status ? (
          <Tag color="success">Completed</Tag>
        ) : (
          <Tag color="error">Failed</Tag>
        ),
    },
    {
      title: "Actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            shape="circle"
            icon={<EyeOutlined />}
            onClick={() => window.open(record.imgurl, "_blank")}
          />

          <Button
            shape="circle"
            icon={<DownloadOutlined />}
            onClick={() => downloadImage(record.imgurl)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full max-w-full overflow-x-hidden bg-[#f8f9ff] p-4 md:p-6 text-slate-900">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-7">
          <h1 className="text-3xl font-extrabold m-0">History</h1>
          <p className="text-slate-600 mt-2">
            View all your image optimizations and shipping savings
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-7">
          <StatsCard
            icon={<Wallet size={32} />}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            title="Total Optimizations"
            value={stats?.totalOptimizations}
            subtitle="All time"
          />

          <StatsCard
            icon={<IndianRupee size={32} />}
            iconBg="bg-green-100"
            iconColor="text-green-600"
            title="Total Savings"
            value={`₹${stats?.totalSavings ? stats?.totalSavings : 0}`}
            valueColor="text-green-600"
            subtitle="All time shipping saved"
          />

          <StatsCard
            icon={<TrendingUp size={32} />}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            title="Average Savings"
            value={`₹${stats?.averageSavings ? stats?.averageSavings : 0}`}
            valueColor="text-blue-600"
            subtitle="Per optimization"
          />

          <StatsCard
            icon={<Briefcase size={32} />}
            iconBg="bg-orange-100"
            iconColor="text-orange-600"
            title="Credits Used"
            value={stats?.totalCreditsUsed}
            subtitle="Total credits spent"
          />
        </div>

        <Card className="rounded-2xl shadow-sm overflow-hidden ">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <h2 className="text-xl font-bold m-0">Optimization History</h2>

            {/* <Space>
              <Select
                defaultValue="all"
                className="w-[150px]"
                options={[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "week", label: "This Week" },
                  { value: "month", label: "This Month" },
                ]}
                suffixIcon={<CalendarOutlined />}
              />

              <Button icon={<FilterOutlined />}>Filters</Button>
            </Space> */}
          </div>

          {/* <Table
            loading={loading}
            columns={columns}
            dataSource={history}
            rowKey="_id"
            scroll={{ x: 1200 }}
            pagination={false}


          /> */}

          <div className="w-full max-w-[calc(100vw-120px)] overflow-x-auto">
            <div className="min-w-[1300px]">
              <Table
                loading={loading}
                columns={columns}
                dataSource={history}
                rowKey="_id"
                pagination={false}
                scroll={{ x: 1300 }}
              />
            </div>
          </div>
        </Card>



        <CustomPagination
          current={currentPage}
          pageSize={pageSize}
          total={totalRecords}
          onChange={(page) => {
            setCurrentPage(page);
          }}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
}

function StatsCard({
  icon,
  iconBg,
  iconColor,
  title,
  value,
  valueColor = "text-slate-900",
  subtitle,
}) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-5">
        <div
          className={`w-16 h-16 rounded-full ${iconBg} ${iconColor} flex items-center justify-center shrink-0`}
        >
          {icon}
        </div>

        <div>
          <p className="text-slate-700 m-0">{title}</p>
          <h2 className={`text-3xl font-extrabold my-2 ${valueColor}`}>
            {value}
          </h2>
          <p className="text-slate-500 m-0">{subtitle}</p>
        </div>
      </div>
    </Card>
  );
}